/**
 * ERP-FIN-02 Gate AR-02/AR-03/AR-04: AR Master Data Admin (MDM-STD-01)
 * Route: /ar/master-data  (ar.mdm.manage OR ar.receipt.view for read-only)
 *
 * PAY-01B: Added UPI Merchants tab for tenant-scoped merchant VPA configuration.
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Archive, CheckCircle2, RefreshCw, Settings, Smartphone, ShieldAlert } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { arCreditNoteApi, payConfigApi, formatError } from "@/services/api";

const MDM_TABS = [
  // ── AR-02 ──────────────────────────────────────────────────────────────────
  { key: "RECEIPT_METHOD",            label: "Receipt Methods",           group: "Receipts" },
  { key: "ADVANCE_CATEGORY",          label: "Advance Categories",        group: "Receipts" },
  { key: "AGING_BUCKET",              label: "Aging Buckets",             group: "Statements" },
  { key: "REVERSAL_REASON_CODE",      label: "Reversal Codes",            group: "Receipts" },
  { key: "APPROVAL_THRESHOLD",        label: "Approval Thresholds",       group: "Receipts" },
  { key: "ALLOCATION_POLICY",         label: "Allocation Policies",       group: "Receipts" },
  { key: "DEPOSIT_POLICY",            label: "Deposit Policies",          group: "Receipts" },
  { key: "NUMBERING_SERIES",          label: "Numbering Series",          group: "Receipts" },
  // ── AR-03 ──────────────────────────────────────────────────────────────────
  { key: "CN_REASON_CODE",            label: "Credit Note Reasons",       group: "Credit Notes" },
  { key: "REFUND_REASON_CODE",        label: "Refund Reasons",            group: "Credit Notes" },
  { key: "REFUND_METHOD",             label: "Refund Methods",            group: "Credit Notes" },
  { key: "CREDIT_APPROVAL_THRESHOLD", label: "Credit Thresholds",         group: "Credit Notes" },
  { key: "CREDIT_POLICY",             label: "Credit Policies",           group: "Credit Notes" },
  // ── AR-04 / MDM-STD-01 ─────────────────────────────────────────────────────
  { key: "STATEMENT_TEMPLATE",        label: "Statement Templates",       group: "Statements" },
  { key: "STATEMENT_DELIVERY",        label: "Delivery Preferences",      group: "Statements" },
  // ── PAY-01B ─────────────────────────────────────────────────────────────────
  { key: "UPI_MERCHANTS",             label: "UPI Merchants",             group: "Payment Config", special: true },
];

const EMPTY_FORM = { item_code: "", display_name: "", description: "", metadata: "{}", sort_order: 99, effective_from: "", effective_until: "" };
const EMPTY_UPI_FORM = { merchant_code: "", merchant_display_name: "", merchant_vpa: "", legal_entity_id: "", payment_channels: "UPI,PHONEPAY", qr_activation_status: "DEMO_CONFIGURATION", notes: "" };

export default function ARMasterDataAdmin() {
  const { user } = useAuth();
  const [activeTab, setActiveTab]     = useState("RECEIPT_METHOD");
  const [items,     setItems]         = useState([]);
  const [loading,   setLoading]       = useState(true);
  const [showCreate, setShowCreate]   = useState(false);
  const [editing,   setEditing]       = useState(null); // item being edited
  const [form,      setForm]          = useState(EMPTY_FORM);
  const [submitting, setSubmitting]   = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  // PAY-01B: UPI Merchant state
  const [upiMerchants,  setUpiMerchants]  = useState([]);
  const [upiLoading,    setUpiLoading]    = useState(false);
  const [showUpiCreate, setShowUpiCreate] = useState(false);
  const [editingUpi,    setEditingUpi]    = useState(null);
  const [upiForm,       setUpiForm]       = useState(EMPTY_UPI_FORM);
  const [upiSubmitting, setUpiSubmitting] = useState(false);

  const hasCap     = (cap) => user?.capabilities?.includes(cap);
  const canManage  = hasCap("ar.mdm.manage");
  const canView    = hasCap("ar.receipt.view");

  const isUpiTab = activeTab === "UPI_MERCHANTS";

  const load = useCallback(async () => {
    if (isUpiTab) {
      setUpiLoading(true);
      try {
        const res = await payConfigApi.listUPIMerchants();
        setUpiMerchants(res.data?.merchants || []);
      } catch (err) {
        toast.error(formatError(err));
      } finally {
        setUpiLoading(false);
      }
      return;
    }
    setLoading(true);
    try {
      const res = await arCreditNoteApi.getMDMConfig(activeTab, showInactive);
      setItems(res.data?.items || []);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [activeTab, showInactive, isUpiTab]);

  useEffect(() => { load(); }, [load]);

  // ── MDM item handlers ──────────────────────────────────────────────────────
  const openCreate = () => { setForm(EMPTY_FORM); setEditing(null); setShowCreate(true); };
  const openEdit   = (item) => {
    setEditing(item);
    setForm({
      item_code:     item.item_code || "",
      display_name:  item.display_name || "",
      description:   item.description || "",
      metadata:      JSON.stringify(item.metadata || {}, null, 2),
      sort_order:    item.sort_order ?? 99,
      effective_from:  item.effective_from || "",
      effective_until: item.effective_until || "",
    });
    setShowCreate(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let parsedMeta = {};
      try { parsedMeta = JSON.parse(form.metadata || "{}"); } catch { toast.error("Invalid JSON in metadata"); setSubmitting(false); return; }

      const payload = {
        config_type:   activeTab,
        item_code:     form.item_code,
        display_name:  form.display_name,
        description:   form.description || null,
        metadata:      parsedMeta,
        sort_order:    parseInt(form.sort_order, 10) || 99,
        effective_from:  form.effective_from || null,
        effective_until: form.effective_until || null,
      };

      if (editing) {
        await arCreditNoteApi.updateMDMItem(activeTab, editing.id, payload);
        toast.success("Item updated");
      } else {
        await arCreditNoteApi.createMDMItem(activeTab, payload);
        toast.success("Item created");
      }
      setShowCreate(false);
      await load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (item) => {
    if (!window.confirm(`Deactivate "${item.display_name}"? This checks for existing references.`)) return;
    try {
      await arCreditNoteApi.deactivateMDMItem(activeTab, item.id, { reason: "Deactivated via admin panel" });
      toast.success(`"${item.display_name}" deactivated`);
      await load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const handleActivate = async (item) => {
    try {
      await arCreditNoteApi.activateMDMItem(activeTab, item.id);
      toast.success(`"${item.display_name}" reactivated`);
      await load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  // ── PAY-01B: UPI Merchant handlers ─────────────────────────────────────────
  const openUpiCreate = () => { setUpiForm(EMPTY_UPI_FORM); setEditingUpi(null); setShowUpiCreate(true); };
  const openUpiEdit   = (m) => {
    setEditingUpi(m);
    setUpiForm({
      merchant_code:           m.merchant_code || "",
      merchant_display_name:   m.merchant_display_name || "",
      merchant_vpa:            m.merchant_vpa || "",
      legal_entity_id:         m.legal_entity_id || "",
      payment_channels:        (m.payment_channels || ["UPI"]).join(","),
      qr_activation_status:    m.qr_activation_status || "DEMO_CONFIGURATION",
      notes:                   m.notes || "",
    });
    setShowUpiCreate(true);
  };

  const handleUpiSubmit = async (e) => {
    e.preventDefault();
    setUpiSubmitting(true);
    try {
      const payload = {
        merchant_code:           upiForm.merchant_code.toUpperCase(),
        merchant_display_name:   upiForm.merchant_display_name,
        merchant_vpa:            upiForm.merchant_vpa,
        legal_entity_id:         upiForm.legal_entity_id || null,
        payment_channels:        upiForm.payment_channels.split(",").map(s => s.trim()).filter(Boolean),
        qr_activation_status:    upiForm.qr_activation_status,
        notes:                   upiForm.notes || null,
      };
      if (editingUpi) {
        await payConfigApi.updateUPIMerchant(editingUpi.id, payload);
        toast.success("UPI merchant updated");
      } else {
        await payConfigApi.createUPIMerchant(payload);
        toast.success("UPI merchant created");
      }
      setShowUpiCreate(false);
      await load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUpiSubmitting(false);
    }
  };

  const handleUpiToggle = async (m) => {
    try {
      await payConfigApi.updateUPIMerchant(m.id, { is_active: !m.is_active });
      toast.success(m.is_active ? "Merchant deactivated" : "Merchant reactivated");
      await load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  if (!canView) return <div style={{ padding: "48px", textAlign: "center", color: "#9CA3AF" }}>Access denied — ar.receipt.view required</div>;

  return (
    <div data-testid="ar-mdm-admin" style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h1 style={{ fontSize: "22px", fontWeight: 700, color: "#111827", margin: 0, display: "flex", alignItems: "center", gap: "8px" }}>
            <Settings size={20} /> AR Master Data
          </h1>
          <p style={{ color: "#6B7280", fontSize: "13px", marginTop: "4px" }}>MDM-STD-01 · Configurable AR reference data · ERP-FIN-02</p>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {!isUpiTab && (
            <label style={{ fontSize: "12px", color: "#6B7280", display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
              <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
              Show Inactive
            </label>
          )}
          {canManage && !isUpiTab && (
            <button data-testid="mdm-add-btn" onClick={openCreate}
              style={{ display: "flex", alignItems: "center", gap: "6px", background: "#0176D3", color: "#fff", border: "none", padding: "8px 14px", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
              <Plus size={14} /> Add Item
            </button>
          )}
          {canManage && isUpiTab && (
            <button data-testid="upi-merchant-add-btn" onClick={openUpiCreate}
              style={{ display: "flex", alignItems: "center", gap: "6px", background: "#7C3AED", color: "#fff", border: "none", padding: "8px 14px", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
              <Plus size={14} /> Add Merchant
            </button>
          )}
          <button onClick={load} style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "6px", padding: "8px 10px", cursor: "pointer", color: "#6B7280" }}>
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Tab Nav */}
      <div style={{ display: "flex", gap: "4px", overflowX: "auto", marginBottom: "20px", background: "#F3F4F6", borderRadius: "8px", padding: "4px" }}>
        {MDM_TABS.map(t => (
          <button key={t.key} data-testid={`mdm-tab-${t.key.toLowerCase()}`} onClick={() => setActiveTab(t.key)}
            style={{ padding: "7px 14px", borderRadius: "6px", border: "none", background: activeTab === t.key ? "#fff" : "transparent", color: activeTab === t.key ? "#111827" : "#6B7280", fontWeight: activeTab === t.key ? 600 : 400, fontSize: "12px", cursor: "pointer", whiteSpace: "nowrap", boxShadow: activeTab === t.key ? "0 1px 3px rgba(0,0,0,.1)" : "none" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Config Items List / UPI Merchants Panel */}
      {isUpiTab ? (
        /* ── PAY-01B: UPI Merchants ── */
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "10px", overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: "#FAFAFA", borderBottom: "1px solid #E5E7EB", display: "flex", alignItems: "center", gap: "8px" }}>
            <Smartphone size={14} style={{ color: "#7C3AED" }} />
            <span style={{ fontSize: "12px", color: "#6B7280" }}>
              UPI merchant VPA configurations for QR-based collections. Finance Owner must authorize production VPAs (FINANCE_VERIFIED).
            </span>
          </div>
          {upiLoading ? (
            <div style={{ padding: "32px", textAlign: "center", color: "#6B7280" }}>Loading…</div>
          ) : upiMerchants.length === 0 ? (
            <div style={{ padding: "32px", textAlign: "center", color: "#9CA3AF", fontSize: "13px" }}>
              No UPI merchants configured. {canManage ? "Click 'Add Merchant' to configure one." : ""}
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
                  {["Code", "Display Name", "Merchant VPA", "Channels", "QR Status", "Active", canManage ? "Actions" : null].filter(Boolean).map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: "11px", fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {upiMerchants.map((m, idx) => (
                  <tr key={m.id} data-testid={`upi-merchant-row-${m.merchant_code}`}
                    style={{ borderBottom: idx < upiMerchants.length - 1 ? "1px solid #F3F4F6" : "none", opacity: m.is_active ? 1 : 0.5 }}>
                    <td style={{ padding: "10px 14px" }}>
                      <code style={{ fontSize: "12px", background: "#F3F4F6", padding: "2px 6px", borderRadius: "4px", fontWeight: 700 }}>{m.merchant_code}</code>
                    </td>
                    <td style={{ padding: "10px 14px", fontSize: "13px", fontWeight: 500, color: "#111827" }}>{m.merchant_display_name}</td>
                    <td style={{ padding: "10px 14px", fontSize: "12px", fontFamily: "monospace", color: "#374151" }}>{m.merchant_vpa}</td>
                    <td style={{ padding: "10px 14px", fontSize: "12px", color: "#6B7280" }}>{(m.payment_channels || []).join(", ")}</td>
                    <td style={{ padding: "10px 14px" }}>
                      {m.qr_activation_status === "FINANCE_VERIFIED" && (
                        <span style={{ fontSize: "11px", padding: "2px 8px", background: "#DCFCE7", color: "#166534", borderRadius: "12px", fontWeight: 600 }}>Production</span>
                      )}
                      {m.qr_activation_status === "DEMO_CONFIGURATION" && (
                        <span style={{ fontSize: "11px", padding: "2px 8px", background: "#FFFBEB", color: "#92400E", borderRadius: "12px", fontWeight: 600 }}>Demo</span>
                      )}
                      {m.qr_activation_status === "INACTIVE" && (
                        <span style={{ fontSize: "11px", padding: "2px 8px", background: "#F5F5F5", color: "#6B7280", borderRadius: "12px", fontWeight: 600 }}>Inactive</span>
                      )}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      {m.is_active
                        ? <span style={{ fontSize: "11px", padding: "2px 8px", background: "#F0FDF4", color: "#166534", borderRadius: "12px", fontWeight: 600 }}>Active</span>
                        : <span style={{ fontSize: "11px", padding: "2px 8px", background: "#F5F5F5", color: "#6B7280", borderRadius: "12px", fontWeight: 600 }}>Inactive</span>}
                    </td>
                    {canManage && (
                      <td style={{ padding: "10px 14px" }}>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button data-testid={`edit-upi-${m.merchant_code}`} onClick={() => openUpiEdit(m)}
                            style={{ background: "none", border: "1px solid #E5E7EB", color: "#374151", padding: "3px 8px", borderRadius: "4px", cursor: "pointer", fontSize: "11px" }}>
                            Edit
                          </button>
                          <button data-testid={`toggle-upi-${m.merchant_code}`} onClick={() => handleUpiToggle(m)}
                            style={{ background: "none", border: `1px solid ${m.is_active ? "#FECACA" : "#BBF7D0"}`, color: m.is_active ? "#DC2626" : "#166534", padding: "3px 8px", borderRadius: "4px", cursor: "pointer", fontSize: "11px" }}>
                            {m.is_active ? "Deactivate" : "Reactivate"}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
      /* ── Standard MDM Items ── */
      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "10px", overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "32px", textAlign: "center", color: "#6B7280" }}>Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: "32px", textAlign: "center", color: "#9CA3AF", fontSize: "13px" }}>
            No items found. {canManage ? "Click 'Add Item' to create the first one." : ""}
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
                {["Code", "Name", "Description", "Sort", "Active", canManage ? "Actions" : null].filter(Boolean).map(h => (
                  <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: "11px", fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={item.id} data-testid={`mdm-item-${item.item_code}`}
                  style={{ borderBottom: idx < items.length - 1 ? "1px solid #F3F4F6" : "none", opacity: item.is_active ? 1 : 0.5 }}>
                  <td style={{ padding: "10px 14px" }}>
                    <code style={{ fontSize: "12px", background: "#F3F4F6", padding: "2px 6px", borderRadius: "4px", fontWeight: 700 }}>{item.item_code}</code>
                  </td>
                  <td style={{ padding: "10px 14px", fontSize: "13px", fontWeight: 500, color: "#111827" }}>{item.display_name}</td>
                  <td style={{ padding: "10px 14px", fontSize: "12px", color: "#6B7280" }}>{item.description || "—"}</td>
                  <td style={{ padding: "10px 14px", fontSize: "12px", color: "#6B7280" }}>{item.sort_order}</td>
                  <td style={{ padding: "10px 14px" }}>
                    {item.is_active
                      ? <span style={{ fontSize: "11px", padding: "2px 8px", background: "#F0FDF4", color: "#166534", borderRadius: "12px", fontWeight: 600 }}>Active</span>
                      : <span style={{ fontSize: "11px", padding: "2px 8px", background: "#F5F5F5", color: "#6B7280", borderRadius: "12px", fontWeight: 600 }}>Inactive</span>}
                    {item.tenant_id === "PLATFORM" && <span style={{ fontSize: "10px", marginLeft: "4px", color: "#9CA3AF" }}>Platform Default</span>}
                  </td>
                  {canManage && (
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{ display: "flex", gap: "6px" }}>
                        {item.tenant_id !== "PLATFORM" && (
                          <button data-testid={`edit-${item.item_code}`} onClick={() => openEdit(item)}
                            style={{ background: "none", border: "1px solid #E5E7EB", color: "#374151", padding: "3px 8px", borderRadius: "4px", cursor: "pointer", fontSize: "11px" }}>
                            Edit
                          </button>
                        )}
                        {item.is_active ? (
                          <button data-testid={`deactivate-${item.item_code}`} onClick={() => handleDeactivate(item)}
                            style={{ background: "none", border: "1px solid #FECACA", color: "#DC2626", padding: "3px 8px", borderRadius: "4px", cursor: "pointer", fontSize: "11px" }}>
                            Deactivate
                          </button>
                        ) : (
                          <button data-testid={`activate-${item.item_code}`} onClick={() => handleActivate(item)}
                            style={{ background: "none", border: "1px solid #BBF7D0", color: "#166534", padding: "3px 8px", borderRadius: "4px", cursor: "pointer", fontSize: "11px" }}>
                            Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )} {/* end isUpiTab conditional */}

      {/* ── MDM Create / Edit Modal ── */}
      {showCreate && canManage && !isUpiTab && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
          <div data-testid="mdm-item-modal" style={{ background: "#fff", borderRadius: "12px", padding: "24px", width: "100%", maxWidth: "520px", maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ fontSize: "16px", fontWeight: 700, marginBottom: "16px", color: "#111827" }}>
              {editing ? "Edit MDM Item" : "Create MDM Item"} — {MDM_TABS.find(t => t.key === activeTab)?.label}
            </h3>
            <form onSubmit={handleSubmit}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={lbl}>Item Code *</label>
                  <input data-testid="mdm-code-input" type="text" required value={form.item_code}
                    onChange={e => setForm(f => ({ ...f, item_code: e.target.value.toUpperCase() }))}
                    disabled={!!editing}
                    placeholder="PAYMENT_METHOD_CODE"
                    style={{ ...inp, width: "100%", boxSizing: "border-box", opacity: editing ? 0.6 : 1 }} />
                </div>
                <div>
                  <label style={lbl}>Sort Order</label>
                  <input data-testid="mdm-sort-input" type="number" min="1" value={form.sort_order}
                    onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
                    style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={lbl}>Display Name *</label>
                  <input data-testid="mdm-name-input" type="text" required value={form.display_name}
                    onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                    placeholder="Human-readable name"
                    style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={lbl}>Description</label>
                  <textarea data-testid="mdm-desc-input" value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    rows={2} style={{ ...inp, width: "100%", boxSizing: "border-box", resize: "vertical" }} />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={lbl}>Metadata (JSON)</label>
                  <textarea data-testid="mdm-meta-input" value={form.metadata}
                    onChange={e => setForm(f => ({ ...f, metadata: e.target.value }))}
                    rows={4} style={{ ...inp, width: "100%", boxSizing: "border-box", resize: "vertical", fontFamily: "monospace", fontSize: "12px" }} />
                </div>
                <div>
                  <label style={lbl}>Effective From</label>
                  <input type="date" value={form.effective_from} onChange={e => setForm(f => ({ ...f, effective_from: e.target.value }))}
                    style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={lbl}>Effective Until</label>
                  <input type="date" value={form.effective_until} onChange={e => setForm(f => ({ ...f, effective_until: e.target.value }))}
                    style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
                <button type="button" onClick={() => setShowCreate(false)} style={{ padding: "7px 16px", border: "1px solid #D1D5DB", borderRadius: "6px", background: "#fff", cursor: "pointer", fontSize: "13px" }}>Cancel</button>
                <button type="submit" data-testid="mdm-save-btn" disabled={submitting}
                  style={{ padding: "7px 16px", background: submitting ? "#9CA3AF" : "#0176D3", color: "#fff", border: "none", borderRadius: "6px", cursor: submitting ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 600 }}>
                  {submitting ? "Saving…" : (editing ? "Update" : "Create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── PAY-01B: UPI Merchant Create / Edit Modal ── */}
      {showUpiCreate && canManage && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
          <div data-testid="upi-merchant-modal" style={{ background: "#fff", borderRadius: "12px", padding: "24px", width: "100%", maxWidth: "540px", maxHeight: "92vh", overflowY: "auto" }}>
            <h3 style={{ fontSize: "16px", fontWeight: 700, marginBottom: "6px", color: "#111827" }}>
              {editingUpi ? "Edit UPI Merchant" : "Add UPI Merchant"} — PAY-01B
            </h3>
            <p style={{ fontSize: "12px", color: "#6B7280", marginBottom: "16px" }}>
              Finance Owner must set status to <strong>FINANCE_VERIFIED</strong> before production QR use.
            </p>
            <form onSubmit={handleUpiSubmit}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={lbl}>Merchant Code *</label>
                  <input data-testid="upi-code-input" type="text" required
                    value={upiForm.merchant_code}
                    onChange={e => setUpiForm(f => ({ ...f, merchant_code: e.target.value.toUpperCase() }))}
                    disabled={!!editingUpi}
                    placeholder="MERCHANT-CODE"
                    style={{ ...inp, width: "100%", boxSizing: "border-box", opacity: editingUpi ? 0.6 : 1 }} />
                </div>
                <div>
                  <label style={lbl}>QR Status *</label>
                  <select data-testid="upi-status-select"
                    value={upiForm.qr_activation_status}
                    onChange={e => setUpiForm(f => ({ ...f, qr_activation_status: e.target.value }))}
                    style={{ ...inp, width: "100%", boxSizing: "border-box" }}>
                    <option value="DEMO_CONFIGURATION">Demo Configuration</option>
                    <option value="FINANCE_VERIFIED">Finance Verified (Production)</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={lbl}>Merchant Display Name *</label>
                  <input data-testid="upi-name-input" type="text" required
                    value={upiForm.merchant_display_name}
                    onChange={e => setUpiForm(f => ({ ...f, merchant_display_name: e.target.value }))}
                    placeholder="e.g. Prashasta Events"
                    style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={lbl}>Merchant UPI VPA *</label>
                  <input data-testid="upi-vpa-input" type="text" required
                    value={upiForm.merchant_vpa}
                    onChange={e => setUpiForm(f => ({ ...f, merchant_vpa: e.target.value }))}
                    placeholder="merchant@bankhandle (e.g. prashasta@hdfc)"
                    style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
                  <p style={{ fontSize: "11px", color: "#6B7280", margin: "2px 0 0" }}>
                    Use DEMO values for UAT. Finance Owner must verify before production use.
                  </p>
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={lbl}>Payment Channels (comma-separated)</label>
                  <input data-testid="upi-channels-input" type="text"
                    value={upiForm.payment_channels}
                    onChange={e => setUpiForm(f => ({ ...f, payment_channels: e.target.value }))}
                    placeholder="UPI,PHONEPAY"
                    style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={lbl}>Notes</label>
                  <textarea value={upiForm.notes}
                    onChange={e => setUpiForm(f => ({ ...f, notes: e.target.value }))}
                    rows={2} placeholder="Configuration notes / approval reference"
                    style={{ ...inp, width: "100%", boxSizing: "border-box", resize: "vertical" }} />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
                <button type="button" onClick={() => setShowUpiCreate(false)}
                  style={{ padding: "7px 16px", border: "1px solid #D1D5DB", borderRadius: "6px", background: "#fff", cursor: "pointer", fontSize: "13px" }}>Cancel</button>
                <button type="submit" data-testid="upi-merchant-save-btn" disabled={upiSubmitting}
                  style={{ padding: "7px 16px", background: upiSubmitting ? "#9CA3AF" : "#7C3AED", color: "#fff", border: "none", borderRadius: "6px", cursor: upiSubmitting ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 600 }}>
                  {upiSubmitting ? "Saving…" : (editingUpi ? "Update" : "Create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const lbl = { display: "block", fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "4px" };
const inp = { padding: "8px 10px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "13px" };
